<script setup lang="ts">
import type { Gender } from "@/model/app/gender"
import { defineTranslations } from "@/utils/defineTranslations"
import { $date, useHttpClient } from "@/vf"
import { computed, inject } from "vue"
import { useI18n } from "vue-i18n"
import { TaskViewMode } from "../../Issue/util"

const props = defineProps<{
    teamId?: string
    registrationId?: string
}>()

interface ShowTeamData {
    numberOfParticipants: number
    numberOfAlternates: number
    numberOfEntourages: number
    numberOfNamedParticipants: number
    numberOfNamedAlternates: number
    numberOfNamedEntourages: number
    members: {
        id: string
        genderForChampionship: Gender
        firstName: string
        lastName: string
        birthDate: string
        gender: string
        pictureApproval: boolean
        roles: ("participant" | "alternate" | "coach" | "supervisor")[]
        blockers: {
            type: string
            columns: ("participant" | "alternate" | "coach" | "supervisor")[]
        }[]
    }[]
}

const http = useHttpClient()
const { data: team } = await http.get<ShowTeamData>(
    `/championship/registration/${props.registrationId}/team/${props.teamId}/table`,
)

const viewMode = inject<"customer" | "admin">(TaskViewMode, "customer")

const { t } = useI18n()

const columns = [
    ["participant", t("@tasks:tasks.championship_registration.team_list.participants")],
    ["alternate", t("@tasks:tasks.championship_registration.team_list.alternates")],
    ["coach", t("@tasks:tasks.championship_registration.team_list.coaches")],
    ["supervisor", t("@tasks:tasks.championship_registration.team_list.supervisors")],
] as const

type ColumnType = (typeof columns)[number][0]

type ShowTeamMember = ShowTeamData["members"][number]

/**
 * Returns true if the member has blockers for the given column.
 */
function memberHasBlockersForColumn(column: ColumnType, member: ShowTeamMember): boolean {
    return member.blockers?.some(b => b.columns.includes(column)) ?? false
}

/**
 * Render the label that is displayed above the columns for each type of member.
 */
function getColumnCountLabel(column: ColumnType): string {
    const { total, named } = getColumnNamedAndTotalMembersCount(column)

    switch (column) {
        case "participant":
            return t("@tasks:tasks.championship_registration.team_list.number_of_participants", total, {
                named: {
                    total,
                    named,
                },
            })
        case "alternate":
            return t("@tasks:tasks.championship_registration.team_list.number_of_alternates", total, {
                named: {
                    total,
                    named,
                },
            })
        case "coach":
            return t("@tasks:tasks.championship_registration.team_list.coaches", total)
        case "supervisor":
            return t("@tasks:tasks.championship_registration.team_list.supervisors", total)
    }
}

/**
 * Returns the class that is applied to the column label, depending on the number of named members.
 * If there are more named members than the total number, the class "text-danger" is returned.
 */
function getColumnLabelClass(column: ColumnType): string {
    const { total, named } = getColumnNamedAndTotalMembersCount(column)

    return total > 0 && named > total ? "text-danger" : ""
}

function getColumnNamedAndTotalMembersCount(column: ColumnType): { total: number; named: number } {
    let total: number = 0
    let named: number = 0

    switch (column) {
        case "participant":
            total = team.numberOfParticipants
            named = team.numberOfNamedParticipants
            break
        case "alternate":
            total = team.numberOfAlternates
            named = team.numberOfNamedAlternates
            break
    }

    return { total, named }
}

const entouragesCount = computed<{ total: number; named: number }>(() => {
    return {
        total: team.numberOfEntourages,
        named: team.numberOfNamedEntourages,
    }
})

const trans = defineTranslations({
    no_team_members: {
        de: 'Es wurden noch keine Mitglieder in diesem Team namentlich gemeldet. Du hast dafür die Möglichkeit im Schritt "Teamauswahl" und "Team finalisieren" in der Meisterschaftsanmeldung.',
        en: 'No members have been registered by name in this team yet. You have the option to do so in the "Team Selection" and "Finalize Team" steps of the championship registration.',
    },
})
</script>

<template>
    <div v-if="team.members.length === 0" class="p-5">
        <div class="text-muted text-center py-5">
            {{ trans("no_team_members") }}
        </div>
    </div>
    <table class="table-reset table table-borderless table-striped" v-else>
        <thead class="bg-white">
            <tr class="bg-white">
                <th colspan="5"></th>
                <th colspan="2">
                    {{
                        $t(
                            "@tasks:tasks.championship_registration.team_list.number_of_entourages",
                            entouragesCount.total,
                            {
                                named: {
                                    total: entouragesCount.total,
                                    named: entouragesCount.named,
                                },
                            },
                        )
                    }}
                </th>
            </tr>
            <tr>
                <th class="align-bottom">{{ $t("@app:organization_member.name") }}</th>
                <th class="align-bottom">{{ $t("@app:organization_member.birthDate") }}</th>
                <th class="align-bottom">{{ $t("@app:organization_member.gender") }}</th>
                <th class="align-bottom">
                    {{ $t("@tasks:tasks.championship_registration.team_list.picture_approval") }}
                </th>
                <th v-for="column in columns" :key="column[0]" :class="getColumnLabelClass(column[0])">
                    {{ getColumnCountLabel(column[0]) || column[1] }}
                </th>
                <th></th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="member in team.members" :key="member.id">
                <td class="text-nowrap">{{ member.firstName }} {{ member.lastName }}</td>
                <td>{{ $date(member.birthDate) }}</td>
                <td>{{ $t("crud:gender." + member.gender) }}</td>
                <td>
                    <i
                        class="fa fa-fw"
                        :class="{
                            'fa-circle-check text-success': member.pictureApproval,
                            'fa-times-circle text-danger': !member.pictureApproval,
                        }"
                    ></i>
                </td>
                <td v-for="column in columns" :key="column[0]">
                    <input
                        type="checkbox"
                        class="custom-checkbox c-default"
                        :class="{
                            'checkbox-office-warning':
                                viewMode === 'admin' &&
                                member.roles.includes(column[0]) &&
                                memberHasBlockersForColumn(column[0], member),
                        }"
                        readonly
                        @click.prevent.stop
                        :checked="member.roles.includes(column[0])"
                    />
                </td>
                <td></td>
            </tr>
        </tbody>
    </table>
</template>

<style scoped>
.checkbox-office-warning {
    background-color: #fd6e6e !important;
}
</style>
